import { QueryKeys, STORAGE, STORAGE_KEY } from "@/constants";
import type { RatingRequest, RatingResponse } from "@/model/rating";
import { useMutation } from "@tanstack/react-query";
import useUserInfo from "./use-user-info";
import { useNotificationError } from "@/context/error-provider";
import { ErrorNotificationType } from "@/pages/components/error-notification";
import { getErrorMessage } from "@/lib/error-message-utils";
import { formatDateTime } from "@/lib/date-utils";
import { setItem } from "@/lib/storage-utils";
import { fetchRating } from "@/services/rating";

type RatingForm = {
  ratingCount: number;
  comment: string;
};

export const useRating = () => {
  const userInfo = useUserInfo();
  const { addError } = useNotificationError();

  return useMutation({
    mutationKey: [QueryKeys.RATING, userInfo?.accessToken, userInfo?.memberId],
    mutationFn: (formData: RatingForm) => {
      const requestInfo: RatingRequest = {
        loginGroupId: userInfo?.loginGroupId || "",
        memberId: userInfo?.memberId || "",
        mbrFirstName: userInfo?.firstname || "",
        groupId: userInfo?.groupId || "",
        divisionId: userInfo?.divisionId || "",
        createdAt: formatDateTime(new Date().toISOString()), // format as YYYY-MM-DD HH:mm:ss
        appVersion: "",
        os: "Web",
        ratingCount: formData?.ratingCount || 0,
        comment: formData?.comment,
        token: userInfo?.accessToken,
      };
      return fetchRating(requestInfo);
    },
    onSuccess: (response: RatingResponse) => {
      if (response?.summary?.status_code === "REQ-200") {
        setItem(
          STORAGE_KEY.APP_INTERACTION,
          { isRated: true },
          STORAGE.SESSION,
        );
        addError({
          message: response?.summary?.status_message,
          type: ErrorNotificationType.INFO,
        });
      } else {
        addError({
          message: response?.summary?.status_message,
          type: ErrorNotificationType.ERROR,
        });
      }
    },
    onError: (error) => {
      addError({
        message: getErrorMessage(error),
        type: ErrorNotificationType.ERROR,
      });
    },
  });
};
